import { useState } from 'react';
import {
  TablePagination,
  Box,
  Typography,
  Collapse,
  Table,
  TableBody,
  TableRow,
  TableHead,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import {
  DialogContainer,
  DialogBody,
  CustomButton,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../../common/components';
import { useExcelExport } from '../../hooks/useExcelExport';
import { usePDFExport } from '../../hooks/usePDFExport';
import { formatCurrency, formatFeeNumbers, formatNumber } from '../../utils/statisticsUtils';
import StatisticsDialogTitle from '../common/StatisticsDialogTitle';

const FeesByOrderModal = ({ open, handleClose, header, results }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [expandedType, setExpandedType] = useState(null);

  const { exportMultipleSheets } = useExcelExport();
  const { exportComplexToPDF } = usePDFExport();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleExpand = (index, type) => {
    if (expandedOrder === index && expandedType === type) {
      setExpandedOrder(null);
      setExpandedType(null);
    } else {
      setExpandedOrder(index);
      setExpandedType(type);
    }
  };

  const calculateThirdDueTotal = (coupons) => {
    return coupons.reduce((total, coupon) => total + (coupon.third_due_amount || 0), 0);
  };

  const handleExportToExcel = () => {
    const sheets = [];

    const resumenData = results.map((result) => ({
      'Número de Contrato': result.contract_header.contract_number,
      Suscriptor: result.contract_header.subscriber_name,
      Plan: result.contract_header.plan_name,
      Grupo: result.contract_header.group_name,
      'Cuotas Emitidas': result.total_cuotas_emitidas,
      'Cuotas Cobradas': result.total_cuotas_cobradas,
      'Cuotas Adeudadas': result.total_cuotas_adeudadas,
      'Cuotas Adelanto': result.total_cuotas_adelanto,
      'Cuotas Puntaje': result.total_cuotas_puntaje,
      'Cuotas Adjudicación': result.total_cuotas_adjudicacion,
      'Cuotas Diferimiento Pagadas': result.cuotas_diferimiento_pagadas,
      'Cuota de Inicio': result.cuota_inicio,
      'Total Pagado': result.total_suma_pagada,
      'Total Sin Interés': result.total_suma_sin_interes,
    }));

    sheets.push({ data: resumenData, name: 'Resumen' });

    results.forEach((result, index) => {
      if (result.todos_los_cupones && result.todos_los_cupones.length > 0) {
        const contractData = result.todos_los_cupones.map((coupon) => ({
          'Cuota #1': coupon.fee_number1,
          'Cuota #2': coupon.fee_number2,
          Mes: coupon.month,
          Año: coupon.year,
          '1er Vencimiento': coupon.first_due_date,
          'Monto 1er Vto': coupon.first_due_amount,
          '2do Vencimiento': coupon.second_due_date,
          'Monto 2do Vto': coupon.second_due_amount,
          '3er Vencimiento': coupon.third_due_date,
          'Monto 3er Vto': coupon.third_due_amount,
          'Fecha Cancelación': coupon.cancellation_date || 'No cancelado',
        }));

        const sheetName = `Contrato${result.contract_header.contract_number}`.substring(0, 31);
        sheets.push({ data: contractData, name: sheetName });
      }

      if (result.diferimientos_pagados && result.diferimientos_pagados.length > 0) {
        const difPagoData = result.diferimientos_pagados.map((pago) => ({
          'ID Pago': pago.uuid,
          'Monto Pago': pago.amount,
          'Cuota Pura Snapshot': pago.cuota_pura_snapshot,
          'Cuotas Pagadas Dif.': pago.cuotas_pagadas,
          'Fecha Pago': pago.paid_at || '-',
        }));
        const sheetName = `DifPago${result.contract_header.contract_number}`.substring(0, 31);
        sheets.push({ data: difPagoData, name: sheetName });
      }
    });

    exportMultipleSheets(sheets, 'CuotasPorOrden');
  };

  const handleExportToPDF = () => {
    const sections = results.map((result) => ({
      title: `Contrato: ${result.contract_header.contract_number} - ${result.contract_header.subscriber_name}`,
      subtitle: `Plan: ${result.contract_header.plan_name} - Grupo: ${result.contract_header.group_name}`,
      data: [
        `Cuotas Emitidas: ${formatNumber(result.total_cuotas_emitidas)}`,
        `Cuotas Cobradas: ${formatNumber(result.total_cuotas_cobradas)}`,
        `Cuotas Adeudadas: ${formatNumber(result.total_cuotas_adeudadas)}`,
        `Cuotas Dif. Pagadas: ${formatNumber(result.cuotas_diferimiento_pagadas || 0)}`,
        `Total Pagado: ${formatCurrency(result.total_suma_pagada)}`,
      ],
    }));

    exportComplexToPDF({
      title: 'Reporte de cuotas por orden',
      sections,
      filename: 'CuotasPorOrden',
      orientation: 'landscape',
    });
  };

  if (!header || !results || results.length === 0) {
    return null;
  }

  const paginatedResults = results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <StatisticsDialogTitle
        title="Cuotas por Orden"
        onExportExcel={handleExportToExcel}
        onExportPDF={handleExportToPDF}
        onClose={handleClose}
      />
      <DialogBody>
        <Box
          sx={{
            mb: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'primary.main',
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
            {header.constructora_info}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
            Actualizado a la fecha: {header.updated_at_date}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            {header.date_filter_text}
          </Typography>
        </Box>
        <Box sx={{ border: '1px solid #ddd', borderRadius: 2, overflow: 'hidden' }}>
          {paginatedResults.map((result, index) => (
            <Box key={index} sx={{ borderBottom: '1px solid #eee' }}>
              <Box sx={{ backgroundColor: '#f4f6f8', p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                  Orden: {result.contract_header.contract_number}
                </Typography>
                <Typography variant="body1" sx={{ color: '#333' }}>
                  Suscriptor: {result.contract_header.subscriber_name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>
                  Plan: {result.contract_header.plan_name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>
                  Grupo: {result.contract_header.group_name}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <CustomButton
                    variant="base"
                    size="small"
                    onClick={() => toggleExpand(index, 'adeudados')}
                    endIcon={
                      expandedOrder === index && expandedType === 'adeudados' ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    }
                  >
                    {expandedOrder === index && expandedType === 'adeudados'
                      ? 'Ocultar cupones adeudados'
                      : 'Ver cupones adeudados'}
                  </CustomButton>
                  <CustomButton
                    variant="base"
                    size="small"
                    onClick={() => toggleExpand(index, 'todos')}
                    endIcon={
                      expandedOrder === index && expandedType === 'todos' ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    }
                  >
                    {expandedOrder === index && expandedType === 'todos'
                      ? 'Ocultar resumen de cupones'
                      : 'Ver resumen de cupones'}
                  </CustomButton>
                  <CustomButton
                    variant="base"
                    size="small"
                    onClick={() => toggleExpand(index, 'puntaje')}
                    endIcon={
                      expandedOrder === index && expandedType === 'puntaje' ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    }
                  >
                    {expandedOrder === index && expandedType === 'puntaje'
                      ? 'Ocultar resumen de puntaje'
                      : 'Ver resumen de puntaje'}
                  </CustomButton>
                  <CustomButton
                    variant="base"
                    size="small"
                    onClick={() => toggleExpand(index, 'adjudicacion')}
                    endIcon={
                      expandedOrder === index && expandedType === 'adjudicacion' ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    }
                  >
                    {expandedOrder === index && expandedType === 'adjudicacion'
                      ? 'Ocultar resumen de adjudicación'
                      : 'Ver resumen de adjudicación'}
                  </CustomButton>
                  <CustomButton
                    variant="base"
                    size="small"
                    onClick={() => toggleExpand(index, 'diferimientoPagado')}
                    endIcon={
                      expandedOrder === index && expandedType === 'diferimientoPagado' ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    }
                  >
                    {expandedOrder === index && expandedType === 'diferimientoPagado'
                      ? 'Ocultar pagos diferimiento'
                      : 'Ver pagos diferimiento'}
                  </CustomButton>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', p: 2, gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    <strong>Cuotas emitidas:</strong> {formatNumber(result.total_cuotas_emitidas)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cuotas cobradas:</strong> {formatNumber(result.total_cuotas_cobradas)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cuotas adeudadas:</strong> {formatNumber(result.total_cuotas_adeudadas)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cuotas adelanto c/puntaje:</strong>{' '}
                    {formatNumber(result.total_cuotas_adelanto)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cuotas puntaje:</strong> {formatNumber(result.total_cuotas_puntaje)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cuotas adjudicación:</strong>{' '}
                    {formatNumber(result.total_cuotas_adjudicacion)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cuotas dif. pagadas:</strong>{' '}
                    {formatNumber(result.cuotas_diferimiento_pagadas || 0)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    <strong>Inicia en cuota:</strong> {result.cuota_inicio}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total pagado:</strong> {formatCurrency(result.total_suma_pagada)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total sin intereses:</strong>{' '}
                    {formatCurrency(result.total_suma_sin_interes)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cuotas adjudicación:</strong>{' '}
                    {formatNumber(result.total_cuotas_adjudicacion)}
                  </Typography>
                </Box>
              </Box>

              <Collapse in={expandedOrder === index && expandedType === 'adeudados'}>
                {result.cupones_adeudados && result.cupones_adeudados.length > 0 && (
                  <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: '#333',
                        color: 'white',
                        p: 1,
                      }}
                    >
                      CUPONES ADEUDADOS
                    </Typography>
                    <GeneralTableBox>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <HeadTableCell>Número Cuota</HeadTableCell>
                            <HeadTableCell>1er Venc.</HeadTableCell>
                            <HeadTableCell>Importe 1er</HeadTableCell>
                            <HeadTableCell>2do Venc.</HeadTableCell>
                            <HeadTableCell>Importe 2do</HeadTableCell>
                            <HeadTableCell>3er Venc.</HeadTableCell>
                            <HeadTableCell>Importe 3er</HeadTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.cupones_adeudados.map((coupon, couponIndex) => (
                            <TableRow key={couponIndex}>
                              <RowTableCell>
                                {formatFeeNumbers(coupon.fee_number1, coupon.fee_number2)}
                              </RowTableCell>
                              <RowTableCell>{coupon.first_due_date}</RowTableCell>
                              <RowTableCell>{formatCurrency(coupon.first_due_amount)}</RowTableCell>
                              <RowTableCell>{coupon.second_due_date}</RowTableCell>
                              <RowTableCell>
                                {formatCurrency(coupon.second_due_amount)}
                              </RowTableCell>
                              <RowTableCell>{coupon.third_due_date}</RowTableCell>
                              <RowTableCell>{formatCurrency(coupon.third_due_amount)}</RowTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </GeneralTableBox>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        p: 1,
                        textAlign: 'right',
                        backgroundColor: '#333',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      TOTAL CUPONES ADEUDADOS:{' '}
                      {formatCurrency(calculateThirdDueTotal(result.cupones_adeudados))}
                    </Typography>
                  </Box>
                )}
              </Collapse>

              <Collapse in={expandedOrder === index && expandedType === 'puntaje'}>
                {result.cupones_puntaje && result.cupones_puntaje.length > 0 && (
                  <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: '#333',
                        color: 'white',
                        p: 1,
                      }}
                    >
                      RESUMEN DE CUPONES DE PUNTAJE
                    </Typography>
                    <GeneralTableBox maxHeight={300}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <HeadTableCell>Importe</HeadTableCell>
                            <HeadTableCell>Cantidad de cuotas</HeadTableCell>
                            <HeadTableCell>Fecha de pago</HeadTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.cupones_puntaje.map((coupon, couponIndex) => (
                            <TableRow key={couponIndex}>
                              <RowTableCell>{formatCurrency(coupon.third_due_amount)}</RowTableCell>
                              <RowTableCell>
                                {coupon.real_score !== null && coupon.real_score !== undefined
                                  ? Number(coupon.real_score).toFixed(2)
                                  : '-'}
                              </RowTableCell>
                              <RowTableCell>{coupon.cancellation_date || '-'}</RowTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </GeneralTableBox>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        p: 1,
                        textAlign: 'right',
                        backgroundColor: '#333',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      TOTAL CUPONES PUNTAJE: {result.cupones_puntaje.length}
                    </Typography>
                  </Box>
                )}
              </Collapse>

              <Collapse in={expandedOrder === index && expandedType === 'adjudicacion'}>
                {result.cupones_adjudicacion && result.cupones_adjudicacion.length > 0 && (
                  <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: '#333',
                        color: 'white',
                        p: 1,
                      }}
                    >
                      RESUMEN DE CUPONES DE ADJUDICACIÓN
                    </Typography>
                    <GeneralTableBox maxHeight={300}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <HeadTableCell>Importe</HeadTableCell>
                            <HeadTableCell>Cant. cuotas</HeadTableCell>
                            <HeadTableCell>Fecha Cancelación</HeadTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.cupones_adjudicacion.map((coupon, couponIndex) => (
                            <TableRow key={couponIndex}>
                              <RowTableCell>{formatCurrency(coupon.third_due_amount)}</RowTableCell>
                              <RowTableCell>
                                {coupon.real_score !== null && coupon.real_score !== undefined
                                  ? Number(coupon.real_score).toFixed(2)
                                  : '-'}
                              </RowTableCell>
                              <RowTableCell>{coupon.cancellation_date || '-'}</RowTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </GeneralTableBox>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        p: 1,
                        textAlign: 'right',
                        backgroundColor: '#333',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      TOTAL CUPONES ADJUDICACIÓN: {result.cupones_adjudicacion.length}
                    </Typography>
                  </Box>
                )}
              </Collapse>
              <Collapse in={expandedOrder === index && expandedType === 'diferimientoPagado'}>
                {result.diferimientos_pagados && result.diferimientos_pagados.length > 0 ? (
                  <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: '#333',
                        color: 'white',
                        p: 1,
                      }}
                    >
                      PAGOS DE DIFERIMIENTO
                    </Typography>
                    <GeneralTableBox maxHeight={300}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <HeadTableCell>ID Pago</HeadTableCell>
                            <HeadTableCell>Fecha Pago</HeadTableCell>
                            <HeadTableCell>Monto</HeadTableCell>
                            <HeadTableCell>Cuota Pura</HeadTableCell>
                            <HeadTableCell>Cuotas Pagadas</HeadTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.diferimientos_pagados.map((pago, pagoIndex) => (
                            <TableRow key={pagoIndex}>
                              <RowTableCell>{pago.uuid}</RowTableCell>
                              <RowTableCell>{pago.paid_at || '-'}</RowTableCell>
                              <RowTableCell>{formatCurrency(pago.amount)}</RowTableCell>
                              <RowTableCell>{formatCurrency(pago.cuota_pura_snapshot)}</RowTableCell>
                              <RowTableCell>{formatNumber(pago.cuotas_pagadas)}</RowTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </GeneralTableBox>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        p: 1,
                        textAlign: 'right',
                        backgroundColor: '#333',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      TOTAL PAGOS DIFERIMIENTO: {result.diferimientos_pagados.length}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ color: '#555' }}>
                      Sin pagos de diferimiento registrados.
                    </Typography>
                  </Box>
                )}
              </Collapse>
              <Collapse in={expandedOrder === index && expandedType === 'todos'}>
                {result.todos_los_cupones && result.todos_los_cupones.length > 0 && (
                  <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: '#333',
                        color: 'white',
                        p: 1,
                      }}
                    >
                      RESUMEN DE CUPONES
                    </Typography>
                    <GeneralTableBox maxHeight={300}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <HeadTableCell>Número Cuota</HeadTableCell>
                            <HeadTableCell>Mes</HeadTableCell>
                            <HeadTableCell>Año</HeadTableCell>
                            <HeadTableCell>1er Venc.</HeadTableCell>
                            <HeadTableCell>Importe 1er</HeadTableCell>
                            <HeadTableCell>2do Venc.</HeadTableCell>
                            <HeadTableCell>Importe 2do</HeadTableCell>
                            <HeadTableCell>3er Venc.</HeadTableCell>
                            <HeadTableCell>Importe 3er</HeadTableCell>
                            <HeadTableCell>Fecha Cancelación</HeadTableCell>
                            <HeadTableCell>Tipo</HeadTableCell>
                            <HeadTableCell>Estado</HeadTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.todos_los_cupones.map((coupon, couponIndex) => {
                            const isCancelled =
                              coupon.cancellation_date && coupon.cancellation_date !== '';
                            const isPending =
                              !isCancelled && new Date(coupon.third_due_date) >= new Date();

                            return (
                              <TableRow
                                key={couponIndex}
                                sx={{
                                  backgroundColor: isCancelled
                                    ? '#e8f5e9'
                                    : isPending
                                    ? '#ffebee'
                                    : '#ff9999',
                                }}
                              >
                                <RowTableCell>
                                  {formatFeeNumbers(coupon.fee_number1, coupon.fee_number2)}
                                </RowTableCell>
                                <RowTableCell>{coupon.month}</RowTableCell>
                                <RowTableCell>{coupon.year}</RowTableCell>
                                <RowTableCell>{coupon.first_due_date}</RowTableCell>
                                <RowTableCell>
                                  {formatCurrency(coupon.first_due_amount)}
                                </RowTableCell>
                                <RowTableCell>{coupon.second_due_date}</RowTableCell>
                                <RowTableCell>
                                  {formatCurrency(coupon.second_due_amount)}
                                </RowTableCell>
                                <RowTableCell>{coupon.third_due_date}</RowTableCell>
                                <RowTableCell>
                                  {formatCurrency(coupon.third_due_amount)}
                                </RowTableCell>
                                <RowTableCell>{coupon.cancellation_date || '-'}</RowTableCell>
                                <RowTableCell>
                                  {coupon.is_score
                                    ? 'Puntaje'
                                    : coupon.is_adjudication
                                    ? 'Adjudicación'
                                    : 'Normal'}
                                </RowTableCell>
                                <RowTableCell>
                                  {isCancelled ? 'Cancelado' : isPending ? 'Pendiente' : 'Vencido'}
                                </RowTableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </GeneralTableBox>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        p: 1,
                        textAlign: 'right',
                        backgroundColor: '#333',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      TOTAL CUPONES: {result.todos_los_cupones.length}
                    </Typography>
                  </Box>
                )}
              </Collapse>
            </Box>
          ))}
        </Box>

        <TablePagination
          component="div"
          count={results.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Órdenes por página"
          rowsPerPageOptions={[10, 25, 50]}
        />
      </DialogBody>
    </DialogContainer>
  );
};

export default FeesByOrderModal;
